import React, {useCallback, useMemo, useState} from 'react';
import type {FieldError, PartTextArea} from 'schemaDefinition/types';
import {evaluateFieldRequired} from 'schemaDefinition/functions';
import {
  toFieldError,
  validateSingleTextValue,
} from 'schemaDefinition/functions/validators';
import {TextAreaField} from 'components';
import type {BaseFieldProps} from './types';
import {useDataFormContext} from '../../contexts';
import {usePartConfiguration} from '../../hooks';

type Props = BaseFieldProps & {
  part: PartTextArea;
};

export const TextArea: React.FC<Props> = ({
  part,
  value,
  setFieldValue,
  valuePath,
  label,
  placeholder,
  localScope,
  globalScope,
  relatedScope,
  mode,
  layout,
}) => {
  const [visited, setVisited] = useState(false);
  const handleSetVisited = useCallback(() => setVisited(true), []);
  const {showErrors} = useDataFormContext();

  const required = useMemo(() => {
    return evaluateFieldRequired(
      part.required,
      valuePath,
      localScope,
      globalScope,
      relatedScope,
      value,
    );
  }, [globalScope, part.required, relatedScope, localScope, value, valuePath]);

  const error = useMemo((): FieldError => {
    if (showErrors || visited) {
      const valid = validateSingleTextValue(part, {
        value,
        valuePath,
        localScope,
        globalScope,
        relatedScope,
      }).valid;
      return toFieldError(valid);
    }
    return false;
  }, [
    globalScope,
    part,
    relatedScope,
    localScope,
    showErrors,
    value,
    valuePath,
    visited,
  ]);
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      // Set empty values to null, to avoid sending empty strings to the backend
      const inputValue = event.target.value || null;
      setFieldValue(valuePath, inputValue);
    },
    [setFieldValue, valuePath],
  );

  const partLayout = usePartConfiguration(part);
  const readonly = part.readonly || mode === 'read-only';

  return (
    <TextAreaField
      name={valuePath}
      label={label}
      placeholder={placeholder}
      value={(value ?? '').toString()}
      required={required}
      readonly={readonly}
      maxLength={part.maxLength}
      rows={readonly ? undefined : partLayout?.rows}
      onChange={handleChange}
      onBlur={handleSetVisited}
      error={error}
      layout={layout}
    />
  );
};
